import Vue from 'vue';
import Vuex from 'vuex';
import * as common from '@/utils/common';
import { jwtHelperMyStage } from '@/utils/jwt';
import logger from '@/modules/Logger';

import uaParser from './modules/uaParser';
import material from './modules/material';
import connection from './modules/connection';
import lesson from './modules/lesson';
import layout from './modules/layout';
import socketio from './modules/socketio';
import devices from './modules/devices';
import triallesson from './modules/triallesson';
import video from './modules/video';
import recorder from './modules/recorder';
import videoRtc from './modules/videoRtc';
import vocabulary from './modules/vocabulary';
import vocabularymanagement from './modules/vocabularymanagement';
import trainerTag from './modules/trainerTag';
import htmlMaterial from './modules/htmlMaterial';
import modal from './modules/modal';
import booking from './modules/booking';
import favorites from './modules/favorites';
import lessonHabit from './modules/lessonHabit';
import customerFeedback from './modules/customerFeedback';
import { bizmatesApi } from '@/utils/bizmatesApi';
import { programs, lessonStatus, providers, errorCodes } from '@/utils/constants';
import qs from 'qs';
import { store } from '@/utils/common';
import { apolloProvider } from '@/modules/GraphQL';
import { GET_MYSTAGE_LESSON_TOKEN } from '@/graphql/mystageLessonToken';
import { STUDENT_OTHER_SETTING } from '@/graphql/student';

Vue.use(Vuex);

export default new Vuex.Store({
  modules: {
    uaParser,
    material,
    connection,
    lesson,
    layout,
    socketio,
    devices,
    triallesson,
    video,
    recorder,
    videoRtc,
    vocabulary,
    vocabularymanagement,
    trainerTag,
    htmlMaterial,
    modal,
    booking,
    favorites,
    lessonHabit,
    customerFeedback
  },
  state: {
    issuedAt: null,
    lessonToken: null,
    lessonId: null,
    userId: null,
    localNickname: null,
    remoteNickname: null,
    accType: null,
    provider: null,
    credential: null,
    programId: null,
    studentRank: null,
    lessonBookRank: null,
    studentLessonNo: null,
    lessonStatus: null,
    lessonStart: null,
    lessonEnd: null,
    gracePeriodEnd: null,
    videoProfile: null,
    screenShareProfile: null,
    jwtError: null,
    unverifiedPayload: null,
    hasNextLesson: null,
    notifyPeriod: null,
    isTest: false,
    isTrial: false,
    jwtKey: null,
    jwtValue: null,
    isFirstLesson: null,
    contractType: null,
    isUserAuth: true,
    chatboxEnd: null,
    instantModal: true,
    bizmates_token: null,
    teacherId: null,
    fuelToken: null,
    remoteUserId: null,
    localExternalId: null,
    remoteExternalId: null,
    hasTranscription: false,
    hasRecording: false,
    hasPopupConfirmation: false,
    displayPopup: false,
    hidePopup: true,
    isRevamp: null,
    unverifiedLessonId: null,
    legacy: null,
    enableBeta: 0
  },
  getters: {
    isStudent: state => {
      return state.accType === 's' || state.isTrial;
    },
    isTrainer: state => {
      return state.accType === 't' && !state.isTrial;
    },
    isBizmatesLesson: state => {
      return state.programId === programs.bizmates;
    },
    isAssessmentLesson: state => {
      return state.programId === programs.assessment;
    },
    isAssistLesson: state => {
      return state.programId === programs.assist_lesson;
    },
    isDiscoveryLesson: state => {
      return state.programId === programs.discovery;
    },
    isOtherLesson: state => {
      return state.programId === programs.other;
    },
    isCorpLesson: state => {
      return state.programId === programs.corporate;
    },
    isWaitingForLesson: state => {
      return state.lessonStatus === lessonStatus.scheduled;
    },
    isLessonOngoing: state => {
      return state.lessonStatus === lessonStatus.ongoing;
    },
    isLessonEnded: state => {
      return (
        state.lessonStatus === lessonStatus.finished ||
        state.lesson.isLessonBeyondGracePeriod ||
        state.socketio.voluntaryLessonTermination
      );
    },
    isWarmUpPeriod: (state) => {
      return state.lessonStatus === lessonStatus.warmup;
    },
    isWaitingOrWarmUpPeriod: (state, getters) => {
      return getters.isWaitingForLesson || getters.isWarmUpPeriod;
    },
    isLessonTest: state => {
      return state.isTest;
    },
    jwtKeyValue: state => {
      return {
        k: state.jwtKey,
        v: state.jwtValue
      };
    },
    logTransportToken: (state, getters) => {
      return state.isTrial
        ? { jwtKeyValue: getters.jwtKeyValue }
        : { lessonToken: state.lessonToken };
    },
    isToEnableVideoLesson: (state, getters) => {
      return (
        state.accType === 's' &&
        (
          state.programId === programs.bizmates ||
          state.programId === programs.other ||
          state.programId === programs.corporate
        ) &&
        !state.isTest &&
        !state.isTrial &&
        !getters.isAssessmentLesson &&
        !getters.isLessonOngoing
      );
    },
    isAmazonChime: state => {
      return state.provider === providers.awschime;
    },
    nonActualLesson(state) {
      return state.isTest || state.isTrial;
    },
    getIat(state) {
      return state.issuedAt;
    },
    isLegacy(state) {
      return state.legacy;
    }
  },
  mutations: {
    getLessonInfo(state, lessonToken) {
      state.lessonToken = lessonToken;

      const lessonInfo = jwtHelperMyStage.verify(lessonToken);

      if (!lessonInfo) {
        state.jwtError = jwtHelperMyStage.jwtError;
        state.unverifiedPayload = jwtHelperMyStage.unverifiedPayload;

        logger.error('[JwtInvalidToken] Invalid token',{
          event_type: 52,
          lesson_id: this.lessonId
        });
        logger.info('Force sending of logs', { operation: 'send-batch-log' });

        if (jwtHelperMyStage.unverifiedPayload.a && jwtHelperMyStage.unverifiedPayload.a === 's') {
          // For students
          jwtHelperMyStage.logToken(null, 'Redirecting to homepage');
          common.redirectToHome(errorCodes.ME001);
        } else {
          // For trainers
          jwtHelperMyStage.logToken(null, 'Redirecting to jwt-error page');
          common.redirectToJwtError();
        }
      }

      state.issuedAt = lessonInfo.iat;
      state.lessonId = lessonInfo.l;
      state.userId = lessonInfo.sub;
      state.remoteUserId = lessonInfo.sub === lessonInfo.s ? lessonInfo.t : lessonInfo.s;
      state.accType = lessonInfo.a;
      state.localNickname = lessonInfo.a === 's' ? lessonInfo.n : lessonInfo.m;
      state.remoteNickname = lessonInfo.a === 's' ? lessonInfo.m : lessonInfo.n;
      state.provider = lessonInfo.w;
      state.credential = lessonInfo.c;
      state.programId = lessonInfo.p;
      state.studentRank = lessonInfo.o;
      state.lessonBookRank = lessonInfo.r;
      state.studentLessonNo = lessonInfo.ln;
      state.lessonStatus = lessonInfo.st;
      state.lessonStart = lessonInfo.b;
      state.lessonStartWarmUp = lessonInfo.b - lessonInfo.wup;
      state.lessonEnd = lessonInfo.e;
      state.gracePeriodEnd = lessonInfo.g;
      state.videoProfile = lessonInfo.vp;
      state.screenShareProfile = lessonInfo.sp;
      state.isTest = lessonInfo.isTest ? true : false;
      state.notifyPeriod = lessonInfo.nt;
      state.isTrial = !!lessonInfo.isTrial;
      state.jwtKey = lessonInfo.k;
      state.jwtValue = lessonInfo.v;
      socketio.state.hasNextLessonStatus = lessonInfo.hnl;
      state.isFirstLesson = lessonInfo.fl;
      state.contractType = lessonInfo.ct;
      state.isUserAuth = true;
      state.chatboxEnd = lessonInfo.cbe;
      state.bizmates_token = lessonInfo.bt;
      state.teacherId = lessonInfo.t;
      state.fuelToken = lessonInfo.ft;
      state.localExternalId = lessonInfo.local_external_id;
      state.remoteExternalId = lessonInfo.remote_external_id;
      state.hasPopupConfirmation = lessonInfo.popConf;
      state.isRevamp = lessonInfo.rv;

      common.checkUserLocalTime(lessonInfo.l);
    },
    activateTranscribe(state, payload) {
      state.hasTranscription = payload;
      store.setTranscribeState(state.lessonId, payload);
    },
    activateAgoraRecord(state, payload) {
      state.hasRecording = payload;
      store.setAgoraRecordState(state.lessonId, payload);
    },
    setIsUserAuth(state, payload) {
      state.isUserAuth = payload;
    },
    showPopupConfirmation(state, payload) {
      state.hasPopupConfirmation = payload;
    },
    getStudentPopup(state, payload){
      state.displayPopup = payload['display_modal'];
      state.hidePopup = payload['hide_modal'];
    },
    setUnverifiedLessonId(state, payload) {
      state.unverifiedLessonId = payload;
    },
    setLegacy(state, payload) {
      if (payload == undefined) {
        return;
      }

      state.legacy = payload;
    },
    setEnableBeta(state, payload) {
      state.enableBeta = payload ?? false;
    }
  },
  actions: {
    async activateTranscribe({ commit }, params) {
      await bizmatesApi.post('lesson/transcribe', qs.stringify(params), {},
        response => {
          commit('activateTranscribe', response.data)
          logger.info('[AWS Transcribe] Transcription is running...');
        },
        error => {
          console.error(error);
        }
      );
    },
    async activateAgoraRecord({ commit }, params) {
      commit('activateAgoraRecord', true);
      await bizmatesApi.post('lesson/agoraRecording', qs.stringify(params), {},
        response => {
          commit('activateAgoraRecord', response.data.status);
          if(response.data.status)
            logger.info('[Agora Cloud] Recording is running...');
          else
            logger.info('[Agora Cloud] Recording is not running... ' + response.data.message);
        },
        error => {
          console.error(error);
        }
      );
    },
    async getLessonInfo({ commit, dispatch }, params) {
      // Request lesson token from Bizmates API backend; when (if) a successful response is received,
      // set the lesson info accordingly (by decoding the lesson token JWT).
      let responseStatus = false;
      commit('setUnverifiedLessonId', params.lessonId);
      
      if (params.legacy) {
        responseStatus = await dispatch('getLegacyMyStageLessonToken', params);
      } else {
        responseStatus = await dispatch('getMyStageLessonToken', params);
      }

      return responseStatus;
    },
    async checkApiMigrationCanaryUser({ commit }, params) {
      const client = apolloProvider.defaultClient;
      // Canary user checking
      let enableBeta = 0; 
      if (!params.legacy) {
        let logPrefix = '[Canary Release] ';
        try {
          const { data } = await client.query({
            query: STUDENT_OTHER_SETTING
          });
          
          enableBeta = data.student_other_setting?.enable_beta;
        } catch (error) {
          logger.error(logPrefix + ' Failed student other setting request, continue as non canary user: ' + error.message, {
            operation: 'lesson:detect-canary-user', 
            lesson_id: params.lessonId || '',
          }); 
        }
        
        let logMessage = enableBeta > 0 ? 'User is included with access level: ' + enableBeta : 'User is not included';
        logger.info(logPrefix + logMessage, {
          operation: 'lesson:detect-canary-user', 
          lesson_id: params.lessonId || '',
        }); 
      }
      
      // commit canary user result for reusability
      commit('setEnableBeta', enableBeta);

      return enableBeta > 0;
    },
    async getMyStageLessonToken({ commit, getters }, params) {
      const client = apolloProvider.defaultClient;
      let responseStatus = false;
      
      try {
        const { data } = await client.query({
          query: GET_MYSTAGE_LESSON_TOKEN,
          fetchPolicy: 'no-cache',
          variables: {
            student_book_id: parseInt(params.lessonId)
          }
        });

        if (data && data.mystage_lesson && data.mystage_lesson.token) {
          commit('getLessonInfo', data.mystage_lesson.token);
          common.sendUserAgentLogData(getters.isStudent);
        } else {
          responseStatus = 'No token data found';
        }
      } catch (error) {
        console.error('GraphQL query failed:', error.message);
        logger.error('Mystage Lesson Token request failed: ' + error.message, { event_type: 36, lesson_id: params.lessonId || '' });
        responseStatus = error;
      }

      return responseStatus;
    },
    async getLegacyMyStageLessonToken({ commit, getters }, params) {
      let responseStatus = false;

      await bizmatesApi.get('lesson/token', {
        params: { id: params.lessonId, t: Date.now() },
        timeout: 10000
      }, response => {
        commit('getLessonInfo', response.data);
        common.sendUserAgentLogData(getters.isStudent);
      }, err => {
        logger.error('Token request failed: ' + err.message,{
          event_type: 36,
          lesson_id: params.lessonId || ''
        }); // handle err to continue the flow of rerequest token functionality
        responseStatus = err;
      });

      return responseStatus;
    },
    async getAdminTrainerLessonInfo({ commit }, params) {
      // Request lesson token from Bizmates API backend; when (if) a successful response is received,
      // set the lesson info accordingly (by decoding the lesson token JWT).
      await bizmatesApi.get('lesson/adminTrainerToken', {
        params: { id: params.lessonId },
        timeout: 3000
      }, response => {
        commit('getLessonInfo', response.data);
      });
    },
    async getTestLessonInfo({ commit }, params) {
      // Will generate test token to be use as a backdoor entry on production environment
      // Request lesson token from Bizmates API backend; when (if) a successful response is received,
      // set the lesson info accordingly (by decoding the lesson token JWT).
      await bizmatesApi.get('lesson/testToken', {
        params: {
          is_student: params.isStudent,
          id: params.lessonId,
          partner: params.partnerId,
          webrtc: params.webrtcId
        },
        timeout: 3000
      }, response => {
        commit('getLessonInfo', response.data);
      });
    },
    async getTrialLessonInfo({ commit }, params) {
      // Request trial lesson token from Bizmates API backend; when (if) a successful response is received,
      // set the lesson info accordingly (by decoding the lesson token JWT).
      await bizmatesApi.get('sales/token', {
        params: {
          id: params.lessonId,
          spci: params.pCompanyId,
          hc: params.hashCode,
          s: params.user
        },
        timeout: 3000
      }, response => {
        commit('getLessonInfo', response.data);
      });
    },
    async getStudentPopup({ commit }, params) {
      let studentPopup = [];
      await bizmatesApi.get('lesson/studentPopup', { 
        params: { userId: params.userId }
      },
      response => {
        studentPopup['student_id'] = response.data.student_id;
        studentPopup['display_modal'] = response.data.display_modal;
        studentPopup['hide_modal'] = response.data.hide_modal;
        commit('getStudentPopup', studentPopup);
      },
      error => {
        studentPopup['student_id'] = null;
        studentPopup['display_modal'] = null;
        studentPopup['hide_modal'] = null;
        console.error(error);
      });

      return studentPopup;
    },
    setLegacy({ commit }, params) {
      commit('setLegacy', params.legacy);
    }
  }
});
